import { Card, Collapse } from "antd";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useState, useEffect } from "react";
import { CaretRightOutlined } from "@ant-design/icons";

const { Panel } = Collapse;

const Privacy = () => {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);

  const LAST_UPDATED = "November 27, 2025";

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const tocSections = [
    { id: "section-1", title: t('privacy_section_1') },
    { id: "section-2", title: t('privacy_section_2') },
    { id: "section-3", title: t('privacy_section_3') },
    { id: "section-4", title: t('privacy_section_4') },
    { id: "section-5", title: t('privacy_section_5') },
    { id: "section-6", title: t('privacy_section_6') },
    { id: "section-7", title: t('privacy_section_7') },
    { id: "section-8", title: t('privacy_section_8') },
    { id: "section-9", title: t('privacy_section_9') }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white">
      <Header />
      <div className="max-w-7xl mx-auto px-4 py-8 text-gray-800">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-indigo-700 mb-2">
            {t('privacy_policy_title')}
          </h1>
          <p className="text-lg text-gray-600">
            {t('last_updated')}: {LAST_UPDATED}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          <div className="lg:hidden">
            <Collapse 
              bordered={false}
              expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
              className="bg-white"
            >
              <Panel header={t('table_of_contents')} key="1" className="font-semibold">
                <div className="space-y-2">
                  {tocSections.map((section, index) => (
                    <a 
                      key={section.id}
                      href={`#${section.id}`}
                      className="block text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg px-3 py-2 transition-colors duration-200"
                    >
                      <span className="font-medium">{index + 1}.</span> {section.title}
                    </a>
                  ))}
                </div>
              </Panel>
            </Collapse>
          </div>

          <div className="hidden lg:block lg:w-1/4">
            <Card className="sticky top-24 bg-indigo-50 border-indigo-200 shadow-sm">
              <h3 className="text-lg font-semibold text-indigo-700 mb-4">{t('table_of_contents')}</h3>
              <div className="space-y-2">
                {tocSections.map((section, index) => (
                  <a 
                    key={section.id}
                    href={`#${section.id}`}
                    className="block text-indigo-600 hover:text-indigo-800 hover:bg-indigo-100 rounded-lg px-3 py-2 transition-colors duration-200"
                  >
                    <span className="font-medium">{index + 1}.</span> {section.title}
                  </a>
                ))}
              </div>
            </Card>
          </div>

          <div className="lg:w-3/4">
            <Card className="shadow-lg rounded-xl">
              <div className="prose max-w-none">
                <h2 id="section-1" className="text-2xl font-bold text-indigo-700 mb-4 scroll-mt-24">
                  {t('privacy_section_1')}
                </h2>
                <p className="mb-4">
                  {t('privacy_section_1_content_1')}
                </p>
                <ul className="list-disc list-inside mb-4 ml-4">
                  <li><strong>{t('privacy_section_1_item_1')}</strong></li>
                  <li><strong>{t('privacy_section_1_item_2')}</strong></li>
                  <li><strong>{t('privacy_section_1_item_3')}</strong></li>
                </ul>
                <p className="mb-3">
                  {t('privacy_section_1_content_2')}
                </p>
                <p className="mb-8">
                  {t('privacy_section_1_content_3')}
                </p>

                <h2 id="section-2" className="text-2xl font-bold text-indigo-700 mb-4 scroll-mt-24">
                  {t('privacy_section_2')}
                </h2>
                <p className="mb-4">
                  {t('privacy_section_2_content_1')}
                </p>
                <ul className="list-disc list-inside mb-4 ml-4">
                  <li>{t('privacy_section_2_item_1')}</li>
                  <li>{t('privacy_section_2_item_2')}</li>
                  <li>{t('privacy_section_2_item_3')}</li>
                  <li>{t('privacy_section_2_item_4')}</li>
                </ul>
                <p className="mb-8">
                  {t('privacy_section_2_content_2')}
                </p>

                <h2 id="section-3" className="text-2xl font-bold text-indigo-700 mb-4 scroll-mt-24">
                  {t('privacy_section_3')}
                </h2>
                <p className="mb-3">
                  {t('privacy_section_3_content_1')}
                </p>
                <p className="mb-3">{t('privacy_section_3_content_2')}</p>
                <ul className="list-disc list-inside mb-8 ml-4">
                  <li>{t('privacy_section_3_item_1')}</li>
                  <li>{t('privacy_section_3_item_2')}</li>
                  <li>{t('privacy_section_3_item_3')}</li>
                </ul>

                <h2 id="section-4" className="text-2xl font-bold text-indigo-700 mb-4 scroll-mt-24">
                  {t('privacy_section_4')}
                </h2>
                <p className="mb-3">
                  {t('privacy_section_4_content_1')}
                </p>
                <ul className="list-disc list-inside mb-8 ml-4">
                  <li>{t('privacy_section_4_item_1')}</li>
                  <li>{t('privacy_section_4_item_2')}</li>
                </ul>

                <h2 id="section-5" className="text-2xl font-bold text-indigo-700 mb-4 scroll-mt-24">
                  {t('privacy_section_5')}
                </h2>
                <p className="mb-4">
                  {t('privacy_section_5_content_1')}
                </p>
                <ul className="list-disc list-inside mb-8 ml-4">
                  <li>{t('privacy_section_5_item_1')}</li>
                  <li>{t('privacy_section_5_item_2')}</li>
                  <li>{t('privacy_section_5_item_3')}</li>
                </ul>

                <h2 id="section-6" className="text-2xl font-bold text-indigo-700 mb-4 scroll-mt-24">
                  {t('privacy_section_6')}
                </h2>
                <p className="mb-3">{t('privacy_section_6_content_1')}</p>
                <ul className="list-disc list-inside mb-3 ml-4">
                  <li>{t('privacy_section_6_item_1')}</li>
                  <li>{t('privacy_section_6_item_2')}</li>
                  <li>{t('privacy_section_6_item_3')}</li>
                </ul>
                <p className="mb-8">
                  {t('privacy_section_6_content_2')}
                </p>

                <h2 id="section-7" className="text-2xl font-bold text-indigo-700 mb-4 scroll-mt-24">
                  {t('privacy_section_7')}
                </h2>
                <p className="mb-4">
                  {t('privacy_section_7_content_1')}
                </p>
                <ul className="list-disc list-inside mb-4 ml-4">
                  <li>{t('privacy_section_7_item_1')}</li>
                  <li>{t('privacy_section_7_item_2')}</li>
                  <li>{t('privacy_section_7_item_3')}</li>
                  <li>{t('privacy_section_7_item_4')}</li>
                </ul>
                <p className="mb-8">
                  {t('privacy_section_7_content_2')}
                </p>

                <h2 id="section-8" className="text-2xl font-bold text-indigo-700 mb-4 scroll-mt-24">
                  {t('privacy_section_8')}
                </h2>
                <p className="mb-3">
                  {t('privacy_section_8_content_1')}
                </p>
                <p className="mb-8">
                  {t('privacy_section_8_content_2')}
                </p>

                <h2 id="section-9" className="text-2xl font-bold text-indigo-700 mb-4 scroll-mt-24">
                  {t('privacy_section_9')}
                </h2>
                <p className="mb-2">{t('privacy_section_9_content_1')}</p>
                <p className="text-indigo-600 font-medium">
                  <strong>{t('email')}:</strong> support@quto.dev<br />
                  <strong>{t('website')}:</strong> https://quto.dev
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Privacy;